import { createSlice, PayloadAction, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../index';
import { onAuthStateChanged, signOut as firebaseSignOut, signInWithPopup, AuthProvider as FirebaseAuthProvider, GoogleAuthProvider, FacebookAuthProvider, Unsubscribe } from 'firebase/auth';
import { doc, getDoc, writeBatch, collection, query, where, getDocs, serverTimestamp, limit, updateDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase'; // Import Firebase config
// Import User and UserProfile types - Assuming they exist in @/types or similar
// Adjust the import path as needed
import type { User as AppUser } from '@/types/firestore'; // Rename imported User to AppUser, import Permission if needed elsewhere
// Import userSpecific actions
import { initializeUserSpecificData, cleanupUserSpecificListeners } from './userSpecificSlice';
import { initializeMyMapsListener, cleanupMapListeners, clearMyMapsData } from './customMapsSlice'; // Import the new action and cleanup
// Import saveEditorSettings from editorSettingsSlice
import { saveEditorSettings } from './editorSettingsSlice';

// Define a specific type for the profile data we actually store/use
// This aligns better with the User type from firestore.ts
interface UserProfileData {
    username: string;
    usernameLower?: string;
    permissions: string[]; // Use string[]
    profilePictureUrl?: string; // Make optional if not always present
    uiTheme?: string; // Make optional if not always present
    createdAt?: string | null; // Store as ISO string for serializability
    updatedAt?: string | null; // Store as ISO string for serializability
    visitedLocationCount?: number;
    isAdmin?: boolean;
    email?: string; // Already optional in firestore.ts
    editorSettings?: {
        highlightStyle?: 'background' | 'border' | 'none';
        highlightColor?: 'blue' | 'green' | 'purple' | 'orange' | 'red';
        highlightsEnabled?: boolean;
    };
}

// Define a type for the editor settings, mirroring EditorSettingsState from editorSettingsSlice
// This avoids a direct dependency if types are kept in sync.
// Alternatively, import EditorSettingsState from editorSettingsSlice if preferred.
interface ProfileEditorSettings {
    highlightStyle: 'background' | 'border' | 'none';
    highlightColor: 'blue' | 'green' | 'purple' | 'orange' | 'red';
    highlightsEnabled: boolean;
}

interface AuthState {
  // Use the Firebase User type for the direct auth object if needed for its properties
  // Or keep a minimal user object consistent with session storage
  user: { // Minimal user info for session/quick access
      id: string;
      email?: string; 
      displayName?: string | null; // Reflect Firebase type
      photoURL?: string | null; // Reflect Firebase type
  } | null;
  profile: UserProfileData | null; // Use the refined UserProfileData
  loading: 'idle' | 'pending' | 'initializing';
  error: string | null;
  permissions: string[]; // Use string[] for AppPermission
  authLoaded: boolean;
}

const initialState: AuthState = {
  user: null,
  profile: null,
  loading: 'initializing',
  error: null,
  permissions: [],
  authLoaded: false,
};

const AUTH_SLICE_NAME = 'auth'; // Define slice name for direct dispatch

// --- Helper Functions ---
const clearSessionStorage = () => {
    sessionStorage.removeItem('authUser');
    sessionStorage.removeItem('userProfile');
    sessionStorage.removeItem('permissions'); // Clear permissions too
};

const saveAuthToSession = (user: AuthState['user'], profile: UserProfileData | null, permissions: string[]) => {
    clearSessionStorage();
    if (user) {
        try {
            // Store minimal user data
            sessionStorage.setItem('authUser', JSON.stringify(user));
            if (profile) {
                sessionStorage.setItem('userProfile', JSON.stringify(profile));
            }
            sessionStorage.setItem('permissions', JSON.stringify(permissions)); // Save permissions
        } catch (e) {
            console.error("Error caching auth state:", e);
            clearSessionStorage(); // Clear if caching fails
        }
    }
};

const loadAuthFromSession = (): { user: AuthState['user']; profile: UserProfileData | null, permissions: string[] } => {
    try {
        const cachedUser = sessionStorage.getItem('authUser');
        const cachedProfile = sessionStorage.getItem('userProfile');
        const cachedPermissions = sessionStorage.getItem('permissions');
        return {
            user: cachedUser ? JSON.parse(cachedUser) : null,
            profile: cachedProfile ? JSON.parse(cachedProfile) : null,
            permissions: cachedPermissions ? JSON.parse(cachedPermissions) : [], // Load permissions
        };
    } catch (e) {
        console.error("Error parsing cached auth state:", e);
        clearSessionStorage(); // Clear corrupted cache
        return { user: null, profile: null, permissions: [] };
    }
};

// --- Async Thunks ---

// Thunk to fetch user profile and permissions
const fetchProfileAndPermissions = async (userId: string): Promise<{ profile: UserProfileData | null; permissions: string[] }> => {
    const userDocRef = doc(db, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
        const firestoreUser = userDocSnap.data() as AppUser; // Assume AppUser has Timestamp fields
        // Map to UserProfileData explicitly, converting Timestamps to ISO strings
        const profileData: UserProfileData = {
            username: firestoreUser.username,
            usernameLower: firestoreUser.usernameLower,
            permissions: firestoreUser.permissions || [],
            profilePictureUrl: firestoreUser.profilePictureUrl,
            uiTheme: firestoreUser.uiTheme,
            // Convert Firestore Timestamps to ISO strings
            createdAt: firestoreUser.createdAt?.toDate().toISOString() || null,
            updatedAt: firestoreUser.updatedAt?.toDate().toISOString() || null,
            visitedLocationCount: firestoreUser.visitedLocationCount,
            isAdmin: firestoreUser.isAdmin,
            email: firestoreUser.email,
            editorSettings: firestoreUser.editorSettings,
        };
        
        return { profile: profileData, permissions: profileData.permissions };
    } else {
        console.warn(`User profile not found for UID: ${userId}`);
        return { profile: null, permissions: ['user'] };
    }
};

export const checkAuthState = createAsyncThunk<
  Unsubscribe, // Returns the unsubscribe function for the listener
  void,
  { dispatch: AppDispatch; rejectValue: string }
>(
    `${AUTH_SLICE_NAME}/checkAuthState`,
    async (_, { dispatch, rejectWithValue }) => {
        const { user: cachedUser, profile: cachedProfile, permissions: cachedPermissions } = loadAuthFromSession();
        if (cachedUser && cachedProfile) { // Ensure profile is also cached for meaningful hydration
            console.log('[Auth] Hydrating from session storage');
            // Dispatch hydrate action to quickly update UI from cache
            // The listener will soon provide the most up-to-date state
            dispatch({ type: `${AUTH_SLICE_NAME}/hydrateFromCache`, payload: { user: cachedUser, profile: cachedProfile, permissions: cachedPermissions } });
        }

        return new Promise<Unsubscribe>((resolve, reject) => {
            const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
                // This listener remains active
                if (firebaseUser) {
                    console.log('[Auth] Firebase auth state changed - User FOUND:', firebaseUser.uid);
                    try {
                        const { profile, permissions } = await fetchProfileAndPermissions(firebaseUser.uid);
                        const user: AuthState['user'] = {
                            id: firebaseUser.uid,
                            email: firebaseUser.email ?? undefined,
                            displayName: firebaseUser.displayName,
                            photoURL: firebaseUser.photoURL, // This will have the updated photoURL
                        };
                        
                        dispatch({ type: `${AUTH_SLICE_NAME}/setAuthData`, payload: { user, profile, permissions } });
                        saveAuthToSession(user, profile, permissions);
                        dispatch(initializeUserSpecificData(user.id));
                        dispatch(initializeMyMapsListener(user.id)); // Initialize user's maps
                    } catch (error) {
                        console.error("Error processing authenticated user:", error);
                        // Fallback to minimal user data if profile fetch fails
                        const user: AuthState['user'] = { 
                            id: firebaseUser.uid, 
                            email: firebaseUser.email ?? undefined, 
                            displayName: firebaseUser.displayName, 
                            photoURL: firebaseUser.photoURL 
                        };
                        dispatch({ type: `${AUTH_SLICE_NAME}/setAuthData`, payload: { user, profile: null, permissions: ['user'] } }); // Grant default 'user' permission
                        saveAuthToSession(user, null, ['user']);
                        dispatch(initializeUserSpecificData(user.id)); 
                        dispatch(initializeMyMapsListener(user.id)); // Initialize user's maps even on profile fetch error
                    }
                } else {
                    console.log('[Auth] Firebase auth state changed - No user.');
                    dispatch({ type: `${AUTH_SLICE_NAME}/setAuthData`, payload: null });
                    clearSessionStorage();
                    dispatch(cleanupUserSpecificListeners());
                    dispatch(cleanupMapListeners()); // Clean up all map listeners
                    dispatch(clearMyMapsData()); // Dispatch clearMyMapsData here
                }
            }, (error) => {
                // This error callback is for the listener itself
                console.error('[Auth] Auth state listener error:', error);
                dispatch({ type: `${AUTH_SLICE_NAME}/setAuthData`, payload: null }); // Clear auth state on listener error
                clearSessionStorage();
                dispatch(cleanupUserSpecificListeners());
                dispatch(cleanupMapListeners()); // Clean up all map listeners
                reject(rejectWithValue('Firebase auth listener error')); // Reject the promise of checkAuthState
            });
            
            // Resolve the promise with the unsubscribe function immediately after listener is attached
            resolve(unsubscribe);
        });
    }
);

export const loginWithProvider = createAsyncThunk<
  void, // No specific return value needed, onAuthStateChanged will handle state update
  string,
  { dispatch: AppDispatch; rejectValue: string }
>(
  `${AUTH_SLICE_NAME}/loginWithProvider`,
  async (providerId, { rejectWithValue }) => {
    let provider: FirebaseAuthProvider;
    switch (providerId.toLowerCase()) {
        case 'google':
            provider = new GoogleAuthProvider();
            break;
        case 'facebook':
            provider = new FacebookAuthProvider();
            break;
        default:
            return rejectWithValue(`Unsupported provider: ${providerId}`);
    }

    try {
        // signInWithPopup will trigger onAuthStateChanged
        await signInWithPopup(auth, provider);
        // No need to return user data here, listener handles it
    } catch (error: any) {
        console.error("Login error:", error);
        return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const logoutUser = createAsyncThunk<
    void, // No specific return value needed
    void,
    { dispatch: AppDispatch; rejectValue: string }
>(
    `${AUTH_SLICE_NAME}/logoutUser`,
    async (_, { rejectWithValue }) => {
        try {
            // firebaseSignOut will trigger onAuthStateChanged
            await firebaseSignOut(auth);
            // Listener will handle clearing session and user-specific data
        } catch (error: any) {
            console.error("Logout error:", error);
            // Even if signout fails, attempt to clear local state as a fallback
            // The listener might not fire if Firebase itself has issues.
            clearSessionStorage(); // Redundant if listener works, safe otherwise
            // dispatch(cleanupUserSpecificListeners()); // Listener should handle this.
            return rejectWithValue(error.message || 'Logout failed');
        }
    }
);

// Thunk to check if username exists
export const checkUsernameExists = createAsyncThunk<
    boolean, // Returns true if exists, false otherwise
    string, // Username to check
    { rejectValue: string } // Config
>(
    `${AUTH_SLICE_NAME}/checkUsernameExists`,
    async (username, { rejectWithValue }) => {
        if (!username || username.length < 3) {
            return false; // Don't check invalid usernames
        }
        const usernameLower = username.toLowerCase();
        const q = query(collection(db, "usernames"), where("usernameLower", "==", usernameLower), limit(1));
        try {
            const querySnapshot = await getDocs(q);
            return !querySnapshot.empty; // True if a doc was found
        } catch (error: any) {
            console.error("Error checking username:", error);
            
            // Handle the case when 'usernames' collection doesn't exist yet
            // Most Firestore errors include a "code" property
            if (error.code === 'permission-denied' || 
                error.code === 'resource-exhausted' || 
                error.code === 'not-found' || 
                error.message?.includes('collection does not exist')) {
                console.log("Usernames collection may not exist yet. Assuming username is available.");
                return false; // If collection doesn't exist, username is definitely available
            }
            
            return rejectWithValue('Failed to check username availability');
        }
    }
);


// Thunk to save initial user profile after signup/username creation
export const saveInitialUserProfile = createAsyncThunk<
    UserProfileData, // Return the created profile data
    { userId: string; username: string; email?: string }, // Input args
    { dispatch: AppDispatch; rejectValue: string; state: RootState } // Config
>(
    `${AUTH_SLICE_NAME}/saveInitialUserProfile`,
    async ({ userId, username, email }, { getState, dispatch, rejectWithValue }) => {
        if (!userId || !username) {
            return rejectWithValue('UserId and username are required');
        }

        const usernameLower = username.toLowerCase();
        const userDocRef = doc(db, 'users', userId);
        const usernameDocRef = doc(db, 'usernames', usernameLower);

        // Check if documents already exist to avoid overwriting
        try {
            const [userDoc, usernameDoc] = await Promise.all([
                getDoc(userDocRef),
                getDoc(usernameDocRef)
            ]);

            if (userDoc.exists()) {
                return rejectWithValue('User profile already exists');
            }

            if (usernameDoc.exists()) {
                return rejectWithValue('Username is already taken');
            }
        } catch (error: any) {
            console.error('Error checking existing documents:', error);
            // If this error is due to collections not existing, we can continue
            // Firestore will create collections when we first write to them
            if (!(error.code === 'permission-denied' || 
                  error.code === 'resource-exhausted' || 
                  error.code === 'not-found' || 
                  error.message?.includes('collection does not exist'))) {
                return rejectWithValue(error.message || 'Failed to check existing documents');
            }
            console.log('Collections may not exist yet, proceeding with creation');
        }

        // --- Admin Check ---
        const isAdminUser = email?.toLowerCase() === 'admin@sodatrack.com';
        const initialPermissions = isAdminUser ? ['user', 'admin'] : ['user'];

        // Prepare the initial profile data matching AppUser structure
        const newUserProfile: Omit<AppUser, 'id'> = {
            username: username,
            usernameLower: usernameLower,
            email: email || '',
            permissions: initialPermissions, // Use determined permissions
            createdAt: serverTimestamp() as any,
            updatedAt: serverTimestamp() as any, // Add this field to match security rules
            uiTheme: 'system',
            profilePictureUrl: '',
            visitedLocationCount: 0,
            isAdmin: isAdminUser, // Set based on email check
            editorSettings: {
                highlightStyle: 'background',
                highlightColor: 'blue',
                highlightsEnabled: true,
            },
        };

        try {
            // Use batch write to ensure both operations succeed or fail together
            const batch = writeBatch(db);
            batch.set(userDocRef, newUserProfile);
            // Make sure the username document contains the usernameLower field too
            batch.set(usernameDocRef, { 
                userId: userId,
                usernameLower: usernameLower 
            });
            await batch.commit();

            console.log('[Auth] Initial user profile created for:', userId);

            // Create a copy of the profile with concrete values for return
            // This avoids issues with serverTimestamp() not being resolved yet
            const profileToReturn: UserProfileData = {
                ...newUserProfile,
                createdAt: new Date().toISOString(), // Convert to ISO string
                updatedAt: new Date().toISOString(), // Convert to ISO string
            };

            // Get the current user object from auth state
            const currentUserAuthData = getState().auth.user;

            // Dispatch setAuthData to update Redux state immediately
            // This ensures that components reacting to auth state changes
            // will see the new profile information without delay.
            if (currentUserAuthData) {
                dispatch(setAuthData({
                    user: currentUserAuthData, // Use the existing user object from state
                    profile: profileToReturn,
                    permissions: profileToReturn.permissions
                }));
            } else {
                // This is a fallback/error case. The onAuthStateChanged listener 
                // should eventually sync the state, but immediate UI updates might be affected.
                console.error('[Auth] Current user data not found in auth state when saving initial profile. Redux state might be temporarily inconsistent until next auth sync.');
            }
            
            // Update session storage (this was already here, ensures session is also up-to-date)
            // Note: setAuthData also updates session storage via saveAuthToSession,
            // but calling it here explicitly ensures it's done with the latest profileToReturn
            // if currentUserAuthData was somehow different from what setAuthData would use from its own logic.
            // However, setAuthData calls saveAuthToSession *after* updating the state, which is generally fine.
            // For consistency, the call within setAuthData is the primary one.
            // The saveAuthToSession call below can be considered redundant if setAuthData handles it.
            // Let's ensure setAuthData is the primary mechanism for session updates after state change.
            // The original code had saveAuthToSession here:
            saveAuthToSession(currentUserAuthData, profileToReturn, profileToReturn.permissions);


            // Make sure we don't initialize user-specific data before the profile exists
            dispatch(initializeUserSpecificData(userId));

            return profileToReturn;
        } catch (error: any) {
            console.error("Error saving initial user profile:", error);
            return rejectWithValue(error.message || 'Failed to save profile');
        }
    }
);

// updateUserProfile and fetchUserProfile remain placeholders for now

// Add updateUserProfile function
export const updateUserProfile = createAsyncThunk<
    UserProfileData,
    Partial<UserProfileData>,
    { state: RootState }
>(
    'auth/updateUserProfile',
    async (profileUpdates, { getState, dispatch, rejectWithValue }) => {
        const { auth } = getState();
        const userId = auth.user?.id;

        if (!userId) {
            return rejectWithValue('User not authenticated');
        }

        try {
            const userDocRef = doc(db, 'users', userId);
            const updateData = {
                ...profileUpdates,
                updatedAt: serverTimestamp()
            };

            // For editor settings, use a more targeted approach to minimize UI impact
            const isOnlyEditorSettingsUpdate = 
                Object.keys(profileUpdates).length === 1 && 
                profileUpdates.editorSettings !== undefined;

            await updateDoc(userDocRef, updateData);

            // For editor settings updates, we can skip the fetch if we have all the data already
            if (isOnlyEditorSettingsUpdate && auth.profile) {
                // Create an updated profile by merging the current profile with the updates
                const updatedProfile = {
                    ...auth.profile,
                    ...profileUpdates,
                    // Ensure editorSettings is properly merged
                    editorSettings: {
                        ...auth.profile.editorSettings,
                        ...profileUpdates.editorSettings
                    }
                };
                
                // Just update session storage without dispatching setAuthData
                saveAuthToSession(auth.user, updatedProfile, auth.permissions);
                
                return updatedProfile;
            } else {
                // For other profile updates, get the full updated profile
                const { profile, permissions } = await fetchProfileAndPermissions(userId);

                // Update state
                if (auth.user) {
                    dispatch(setAuthData({ 
                        user: auth.user, 
                        profile, 
                        permissions 
                    }));
                }

                // Update session storage
                saveAuthToSession(auth.user, profile, permissions);
                
                return profile as UserProfileData;
            }
        } catch (error: any) {
            console.error("Error updating user profile:", error);
            return rejectWithValue(error.message || 'Failed to update profile');
        }
    }
);

// --- Slice Definition ---
const authSlice = createSlice({
  name: AUTH_SLICE_NAME,
  initialState,
  reducers: {
    hydrateFromCache: (state, action: PayloadAction<{ user: AuthState['user']; profile: UserProfileData | null; permissions: string[] }>) => {
        // Only hydrate if still in 'initializing' state to avoid overwriting live data
        if (state.loading === 'initializing' && !state.authLoaded) {
            state.user = action.payload.user;
            state.profile = action.payload.profile;
            state.permissions = action.payload.permissions;
            // Do not set loading to 'idle' or authLoaded to true here; listener will do that.
        }
    },
    setAuthData: (state, action: PayloadAction<{ user: AuthState['user']; profile: UserProfileData | null; permissions: string[] } | null>) => {
        if (action.payload) {
            state.user = action.payload.user;
            state.profile = action.payload.profile;
            state.permissions = action.payload.permissions;
            // Update session storage when auth data is set
            saveAuthToSession(action.payload.user, action.payload.profile, action.payload.permissions);
        } else { // No user / logout
            state.user = null;
            state.profile = null;
            state.permissions = [];
            clearSessionStorage(); // Clear session on logout
        }
        state.loading = 'idle';
        state.error = null;
        state.authLoaded = true;
    },
    setLoading: (state, action: PayloadAction<'idle' | 'pending' | 'initializing'>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.loading = 'idle'; // Typically set to idle when an error occurs
      state.error = action.payload;
    },
    setProfileEditorSettings: (state, action: PayloadAction<ProfileEditorSettings>) => {
      if (state.profile) {
        state.profile.editorSettings = action.payload;
        // Ensure session storage is updated with the modified profile
        saveAuthToSession(state.user, state.profile, state.permissions);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAuthState.pending, (state) => {
          // Set to 'pending' if not already 'initializing' from cache load attempt
          if (state.loading === 'initializing') {
            state.loading = 'pending';
          }
          state.error = null;
      })
      .addCase(checkAuthState.fulfilled, () => {
          // Fulfilled means the listener is attached.
          // The listener itself (via setAuthData) will set loading to 'idle' and authLoaded to true.
          // No direct state mutation here other than perhaps an initial loading state adjustment if needed.
          // state.authLoaded = true; // setAuthData handles this
          // state.loading = 'idle'; // setAuthData handles this
          console.log('[Auth] Auth listener attached successfully.');
      })
      .addCase(checkAuthState.rejected, (state, action) => {
          // This means setting up the listener failed.
          state.authLoaded = true; // Mark auth check as complete (even if failed)
          state.loading = 'idle';
          state.error = action.payload ?? 'Failed to initialize auth listener';
          state.user = null;
          state.profile = null;
          state.permissions = [];
      })
      .addCase(loginWithProvider.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(loginWithProvider.fulfilled, (state) => {
        // Listener (onAuthStateChanged -> setAuthData) handles the actual state update
        state.loading = 'idle'; 
        // state.authLoaded = true; // setAuthData handles this
      })
      .addCase(loginWithProvider.rejected, (state, action) => {
        state.loading = 'idle';
        state.error = action.payload ?? 'Login failed';
        // state.authLoaded = true; // setAuthData or listener error path should handle this
      })
      .addCase(logoutUser.pending, () => {
          // Optional: state.loading = 'pending';
      })
      .addCase(logoutUser.fulfilled, (state) => {
        // Listener (onAuthStateChanged -> setAuthData) handles the actual state update
        state.loading = 'idle';
        // state.authLoaded = true; // setAuthData handles this
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = 'idle';
        state.error = action.payload ?? 'Logout failed';
        // state.authLoaded = true; // setAuthData or listener error path should handle this
        console.error("Logout failed in extraReducers:", state.error);
      })
      .addCase(saveInitialUserProfile.pending, (state) => {
          state.loading = 'pending';
          state.error = null;
      })
      .addCase(saveInitialUserProfile.fulfilled, (state) => {
          // This thunk directly provides new profile data.
          // setAuthData (called within the thunk) will ensure state.user is also aligned if needed.
          // state.profile = action.payload; // This is now handled by setAuthData call within the thunk
          // state.permissions = action.payload.permissions; // Also by setAuthData
          state.loading = 'idle';
          state.error = null;
      })
      .addCase(saveInitialUserProfile.rejected, (state, action) => {
          state.loading = 'idle';
          state.error = action.payload ?? 'Failed to save profile';
      })
      .addCase(updateUserProfile.pending, (state) => {
          state.loading = 'pending';
          state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
          state.loading = 'idle';
          state.error = null;
          state.profile = action.payload;
          state.permissions = action.payload.permissions;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
          state.loading = 'idle';
          state.error = action.payload as string || 'Failed to update profile';
      })
      // Add a case to handle successful saveEditorSettings
      .addCase(saveEditorSettings.fulfilled, (state, action) => {
        if (state.profile) {
          // Update the editorSettings inside the profile with the returned saved settings
          state.profile.editorSettings = action.payload;
          // Update session storage to keep it in sync
          saveAuthToSession(state.user, state.profile, state.permissions);
        }
      });
  },
});

export const { setLoading, setError, hydrateFromCache, setAuthData, setProfileEditorSettings } = authSlice.actions;

// --- Selectors ---
export const selectUser = (state: RootState) => state.auth.user;
export const selectUserProfile = (state: RootState) => state.auth.profile;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectIsAuthLoading = (state: RootState) => 
  state.auth.loading === 'pending' || state.auth.loading === 'initializing';
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectPermissions = (state: RootState) => state.auth.permissions;
export const selectIsAuthenticated = (state: RootState) => !!state.auth.user;
export const selectAuthLoaded = (state: RootState) => state.auth.authLoaded;

export const selectHasPermission = (permission: string) => (state: RootState) => {
    const userPermissions = state.auth.permissions;
    if (userPermissions.includes('admin')) return true;
    return userPermissions.includes(permission);
};

// Selector to get a combined user profile details if needed, including photoURL from user object
export const selectUserProfileDetails = createSelector(
    [selectUser, selectUserProfile],
    (user, profile) => {
        if (!user) return null;
        return {
            id: user.id,
            email: user.email || profile?.email,
            displayName: user.displayName || profile?.username || user.email?.split('@')[0] || 'User',
            photoURL: user.photoURL || profile?.profilePictureUrl, // Prioritize user.photoURL
            username: profile?.username,
            permissions: profile?.permissions || [],
            isAdmin: profile?.isAdmin || false,
            uiTheme: profile?.uiTheme,
        };
    }
);

export default authSlice.reducer; 